import invariant from "invariant";
import mixpanel from "mixpanel-browser";
import React, { useCallback, useEffect, useState } from "react";
import { isURL } from "validator";
import { EnumAssetAssetType } from "../../__generated__/graphql";
import { useModalContext } from "../../contexts/modals";
import useStores from "../../hooks/useStores";
import Button from "../Button";

type ChatLinkInputProps = {
  setAssetIds: React.Dispatch<React.SetStateAction<string[]>>;
};

function ChatLinkInput(props: ChatLinkInputProps) {
  const { setAssetIds } = props;
  const [link, setLink] = useState(""); // State for link input
  const { closeModalAll } = useModalContext();
  const { auth, assets, auditLogs } = useStores();
  const { user } = auth;
  const [isProcessingLink, setIsProcessingLink] = useState(false);
  const [isValidLink, setIsValidLink] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  console.log("Is link valid", isValidLink);

  useEffect(() => {
    checkIsLinkValid(link);
    setErrorMessage("");
  }, [link]);

  const checkIsLinkValid = (link: string) => {
    // Regular expression to match a valid URL
    if (link === "") {
      setIsValidLink(true);
      return;
    }

    console.log("Checking link", link);

    setIsValidLink(isURL(link));
  };

  const submit = useCallback(
    async (e: any) => {
      e.preventDefault();
      setErrorMessage("");

      try {
        setIsProcessingLink(true);

        invariant(user, "User must be logged in to process link");

        const newAsset = await assets.processLinkWithEmbedding({
          content: {
            url: link,
          },
          contentType: "webpage",
          user: {
            id: user.id,
          },
          assetType: EnumAssetAssetType.Import,
        });

        mixpanel.track("link-added-as-context-to-chat", {
          userId: user.id,
          link,
        });

        if (!newAsset) {
          setIsProcessingLink(false);
          return;
        }

        // The web-link-attachment audit log is written server-side by
        // linkForEmbedding; refresh the local counts so the UI stays live.
        auditLogs.fetchAuditLogsForLimits();

        setIsProcessingLink(false);

        setAssetIds((currentAssetIds) => [...currentAssetIds, newAsset.id]);
        closeModalAll();
      } catch (e: any) {
        const errorMessage =
          "Failed to attach link. Try again or choose another link. If problem persists, contact support.";

        setErrorMessage(errorMessage);
      } finally {
        setIsProcessingLink(false);
      }
    },
    [link, user]
  );

  return (
    <form className="w-full" onSubmit={submit}>
      <div className="flex flex-col">
        <div>
          <input
            className="border-gray-700 mt-2 mr-3 mb-1 w-full rounded-md border px-4 py-2 text-sm"
            placeholder="eg. https://www.abc.com"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            disabled={isProcessingLink}
            onBlur={() => {
              checkIsLinkValid(link);
            }}
          />
        </div>

        <Button
          className="mt-4"
          type="submit"
          theme="primary"
          buttonText={isProcessingLink ? "Processing Link..." : "Submit"}
          disabled={isProcessingLink || !isValidLink || link === ""}
          showSpinner={isProcessingLink}
          padding="large"
        />
      </div>
      {errorMessage && <div className="text-red-500 mt-2">{errorMessage}</div>}
      {!isValidLink && <div className="text-red-500 mt-2">Invalid Link</div>}
    </form>
  );
}

export default ChatLinkInput;
